import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import type { ApiResponse } from '@/shared/types';

/* ── shared value types ── */

export type CoverageStatus =
  | 'COMPLETE'
  | 'PARTIAL'
  | 'MISSING'
  | 'UNCONFIRMED'
  | 'NOT_APPLICABLE'
  | 'NEEDS_REVIEW';

export type CoveragePeriodSource =
  | 'EXTRACTED_RANGE'
  | 'ASSUMED_FULL_YEAR'
  | 'SINGLE_DATE'
  | 'UNKNOWN';

export type CoverageConfidence = 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';

export type CoverageCadence = 'PERIODIC' | 'ANNUAL' | 'ONE_OFF';

export type CoverageRequirement = 'REQUIRED' | 'EXPECTED' | 'CONDITIONAL';

export type CoveragePropertySource =
  | 'LINKED'
  | 'REGISTRY'
  | 'EXTRACTED'
  | 'UNASSIGNED';

export interface CoveragePeriod {
  start: string;
  end: string;
  /** Pre-formatted "1 Jul 2024 – 30 Jun 2025". */
  label: string;
  days: number;
}

export interface CoverageDocument {
  documentId: string;
  fileName: string;
  newName: string | null;
  docType: string | null;
  docSubType: string | null;
  /** Direct subtype matches rank ahead of categories found inside another document. */
  categoryMatch: 'PRIMARY' | 'ADDITIONAL';
  matchedSubType: string | null;
  matchedAmount: number | null;
  financialYearEnd: number | null;
  issueDate: string | null;
  /** Period read off the document itself. */
  period: CoveragePeriod | null;
  /** The slice of that period counted toward this year/property/category. */
  contributedPeriod: CoveragePeriod | null;
  periodSource: CoveragePeriodSource;
  periodConfidence: CoverageConfidence;
  crossesFinancialYears: boolean;
  /** Set when the document came from a different year (carried forward). */
  fromFinancialYearEnd: number | null;
  webViewLink: string | null;
  driveFileId: string | null;
  blob: {
    container: string;
    blobName: string;
    contentType?: string | null;
  } | null;
  /**
   * Property the document is currently filed under. `address` is the registry
   * displayName, so it matches the values the property picker offers.
   */
  assignedProperty: {
    propertyId: string | null;
    address: string;
  } | null;
  status: string;
}

export interface CoverageGap extends CoveragePeriod {
  reason: 'NO_DOCUMENTS' | 'PERIOD_GAP';
}

export interface CoverageDuplicate {
  period: CoveragePeriod;
  documentIds: string[];
  fileNames: string[];
}

export interface CategoryCoverage {
  category: string;
  label: string;
  order: number;
  cadence: CoverageCadence;
  requirement: CoverageRequirement;
  status: CoverageStatus;
  /**
   * The same verdict with the staff checklist ignored — what the documents on
   * file alone say. Shown as the scan's reading so a staff decision sits beside
   * it instead of replacing it.
   */
  evidenceStatus: CoverageStatus;
  expectedPeriod: CoveragePeriod | null;
  coveredPeriods: CoveragePeriod[];
  missingPeriods: CoverageGap[];
  coveredDays: number;
  expectedDays: number;
  coveragePercent: number;
  documents: CoverageDocument[];
  undatedDocuments: CoverageDocument[];
  duplicates: CoverageDuplicate[];
  checklist: { values: string[]; comment?: string } | null;
  notes: string[];
}

export interface OtherDocumentGroup {
  docType: string | null;
  docSubType: string | null;
  label: string;
  documents: CoverageDocument[];
}

export interface CoverageTotals {
  documents: number;
  complete: number;
  partial: number;
  missing: number;
  unconfirmed: number;
  needsReview: number;
  notApplicable: number;
}

export interface PropertyCoverage {
  propertyKey: string;
  propertyId: string | null;
  address: string;
  source: CoveragePropertySource;
  ownedPeriod: CoveragePeriod | null;
  expectedPeriod: CoveragePeriod | null;
  ownedDuringYear: boolean;
  categories: CategoryCoverage[];
  otherDocuments: OtherDocumentGroup[];
  totals: CoverageTotals;
}

export interface YearCoverage {
  financialYearEnd: number | null;
  label: string;
  period: CoveragePeriod | null;
  yearlyReturnId: string | null;
  yearlyReturnStatus: string | null;
  properties: PropertyCoverage[];
  otherDocuments: OtherDocumentGroup[];
  totals: CoverageTotals;
}

export interface MissingDocumentRequest {
  financialYearEnd: number;
  financialYearLabel: string;
  propertyKey: string;
  propertyId: string | null;
  propertyAddress: string;
  category: string;
  categoryLabel: string;
  requirement: CoverageRequirement;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  reason: 'NO_DOCUMENTS' | 'PERIOD_GAP' | 'UNCONFIRMED' | 'UNREADABLE_PERIOD';
  period: CoveragePeriod | null;
  /**
   * What we already hold, for "you've sent X, we still need Y" wording. Only
   * set next to a PERIOD_GAP — null when nothing has arrived at all.
   */
  coveredPeriodLabel: string | null;
  /**
   * True when the gap is the entire expected period — nothing received at all.
   * The email leaves the dates out in that case; the document name says it.
   */
  coversWholeExpectedPeriod: boolean;
  /** One-line ask, ready to paste into a client email. */
  requestText: string;
}

export interface TaxDealDocumentCoverage {
  taxDealId: string;
  generatedAt: string;
  options: { minGapDays: number; joinToleranceDays: number };
  years: YearCoverage[];
  requests: MissingDocumentRequest[];
  totals: CoverageTotals;
}

export interface CoverageQueryArgs {
  taxDealId: string;
  /** Gaps shorter than this are ignored as boundary noise (default 7). */
  minGapDays?: number;
  /** Seam tolerated between consecutive periods (default 3). */
  joinToleranceDays?: number;
}

/* ── client email draft ── */

export interface RequestEmailItemInput {
  /** Document to ask for, as it should read (e.g. "Rental Statements"). */
  document: string;
  /** Formatted period; the model may reword it but not change the dates. */
  period?: string | null;
  reason?: MissingDocumentRequest['reason'];
  /** Staff note carried into the line. */
  note?: string;
}

export interface RequestEmailPropertyInput {
  propertyAddress: string;
  items: RequestEmailItemInput[];
}

export interface DraftRequestEmailArgs {
  taxDealId: string;
  /** What to ask for. Omit to let the backend derive it from live coverage. */
  properties?: RequestEmailPropertyInput[];
  financialYears?: number[];
  recipientNames?: string[];
  additionalNotes?: string;
  /* derive-mode only */
  financialYearEnd?: number;
  includeUnconfirmed?: boolean;
  includeUnreadable?: boolean;
}

export interface RequestEmailRecipient {
  firstName: string;
  role: string;
}

export interface TaxDocumentRequestEmailDraft {
  taxDealId: string;
  financialYears: number[];
  recipients: RequestEmailRecipient[];
  source: 'PROVIDED' | 'DERIVED';
  outstanding: RequestEmailPropertyInput[];
  totalRequests: number;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  /** AI when the model wrote it, FALLBACK when the built-in template did. */
  generatedBy: 'AI' | 'FALLBACK';
  fallbackReason?: string;
  generatedAt: string;
  /** Always false — the endpoint drafts, it never sends. */
  sent: false;
}

/* ── API slice ── */

export const taxDocumentsCheckApi = createApi({
  reducerPath: 'taxDocumentsCheckApi',
  baseQuery: customBaseQuery,
  tagTypes: ['DocumentCoverage'],
  endpoints: (builder) => ({
    /** Full coverage grid for a deal — every financial year, property and category. */
    getTaxDocumentCoverage: builder.query<
      TaxDealDocumentCoverage,
      CoverageQueryArgs
    >({
      query: ({ taxDealId, ...params }) => ({
        url: `v1/tax/documents-check/${taxDealId}`,
        method: 'GET',
        params,
      }),
      transformResponse: (r: ApiResponse<TaxDealDocumentCoverage>) => r.data,
      providesTags: (_res, _err, arg) => [
        { type: 'DocumentCoverage', id: arg.taxDealId },
      ],
    }),

    /** One financial year of the grid. */
    getTaxDocumentCoverageYear: builder.query<
      YearCoverage,
      CoverageQueryArgs & { financialYearEnd: number }
    >({
      query: ({ taxDealId, financialYearEnd, ...params }) => ({
        url: `v1/tax/documents-check/${taxDealId}/year/${financialYearEnd}`,
        method: 'GET',
        params,
      }),
      transformResponse: (r: ApiResponse<YearCoverage>) => r.data,
      providesTags: (_res, _err, arg) => [
        {
          type: 'DocumentCoverage',
          id: `${arg.taxDealId}:${arg.financialYearEnd}`,
        },
      ],
    }),

    /** Flat chase list — what to still ask the client for. */
    getTaxDocumentMissing: builder.query<
      MissingDocumentRequest[],
      CoverageQueryArgs
    >({
      query: ({ taxDealId, ...params }) => ({
        url: `v1/tax/documents-check/${taxDealId}/missing`,
        method: 'GET',
        params,
      }),
      transformResponse: (r: ApiResponse<MissingDocumentRequest[]>) => r.data,
      providesTags: (_res, _err, arg) => [
        { type: 'DocumentCoverage', id: `${arg.taxDealId}:missing` },
      ],
    }),

    /**
     * Drafts the client email from the selected outstanding items. Drafting
     * only — nothing is sent, and the response is not cached because each call
     * is a fresh generation.
     */
    draftRequestEmail: builder.mutation<
      TaxDocumentRequestEmailDraft,
      DraftRequestEmailArgs
    >({
      query: ({ taxDealId, ...body }) => ({
        url: `v1/tax/documents-check/${taxDealId}/request-email`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<TaxDocumentRequestEmailDraft>) =>
        r.data,
    }),
  }),
});

export const {
  useGetTaxDocumentCoverageQuery,
  useGetTaxDocumentCoverageYearQuery,
  useGetTaxDocumentMissingQuery,
  useDraftRequestEmailMutation,
} = taxDocumentsCheckApi;
